import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Print component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconPrint extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.2351 1.55346C6.52211 1.26646 6.91137 1.10522 7.31725 1.10522H16.6819C17.0877 1.10522 17.477 1.26646 17.764 1.55346C18.051 1.84047 18.2122 2.22973 18.2122 2.63561V7.34839H20.5837C21.1966 7.34839 21.7843 7.59184 22.2177 8.0252C22.651 8.45855 22.8945 9.0463 22.8945 9.65916V16.6826C22.8945 17.2955 22.651 17.8832 22.2177 18.3166C21.7843 18.7499 21.1966 18.9934 20.5837 18.9934H18.2122V21.3648C18.2122 21.7707 18.051 22.1599 17.764 22.4469C17.477 22.7339 17.0877 22.8952 16.6819 22.8952H7.31725C6.91137 22.8952 6.52211 22.7339 6.2351 22.4469C5.9481 22.1599 5.78687 21.7707 5.78687 21.3648V18.9934H3.41526C2.80241 18.9934 2.21465 18.7499 1.7813 18.3166C1.34795 17.8832 1.10449 17.2955 1.10449 16.6826V9.65916C1.10449 9.0463 1.34795 8.45855 1.7813 8.0252C2.21465 7.59184 2.80241 7.34839 3.41526 7.34839H5.78687V2.63561C5.78687 2.22973 5.9481 1.84047 6.2351 1.55346ZM16.7122 2.63561V7.34839H7.28687V2.63561C7.28687 2.62755 7.29007 2.61982 7.29576 2.61412C7.30146 2.60843 7.30919 2.60522 7.31725 2.60522H16.6819C16.6899 2.60522 16.6977 2.60843 16.7034 2.61412C16.709 2.61982 16.7122 2.62755 16.7122 2.63561ZM16.7122 18.2334L16.7122 18.2434L16.7122 18.2534V21.3648C16.7122 21.3729 16.709 21.3806 16.7034 21.3863C16.6977 21.392 16.6899 21.3952 16.6819 21.3952H7.31725C7.30919 21.3952 7.30146 21.392 7.29576 21.3863C7.29007 21.3806 7.28687 21.3729 7.28687 21.3648V16.6521H16.7122V18.2334ZM18.2122 17.4934H20.5837C20.7988 17.4934 21.005 17.408 21.157 17.2559C21.3091 17.1039 21.3945 16.8976 21.3945 16.6826V9.65916C21.3945 9.44413 21.3091 9.23791 21.157 9.08586C21.005 8.93381 20.7988 8.84839 20.5837 8.84839H3.41526C3.20023 8.84839 2.99401 8.93381 2.84196 9.08586C2.68991 9.23791 2.60449 9.44413 2.60449 9.65916V16.6826C2.60449 16.8976 2.68991 17.1039 2.84196 17.2559C2.99401 17.408 3.20023 17.4934 3.41526 17.4934H5.78687V15.9021C5.78687 15.4879 6.12265 15.1521 6.53687 15.1521H17.4622C17.8765 15.1521 18.2122 15.4879 18.2122 15.9021V17.4934ZM16.6819 11.2502C16.2677 11.2502 15.9319 11.586 15.9319 12.0002C15.9319 12.4145 16.2677 12.7502 16.6819 12.7502H18.2427C18.6569 12.7502 18.9927 12.4145 18.9927 12.0002C18.9927 11.586 18.6569 11.2502 18.2427 11.2502H16.6819Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-print') === undefined) {
  customElements.define('cat-icon-print', BlocksIconPrint);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-print': BlocksIconPrint;
  }
}
